"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { track, type VirtuosaEvent } from "@/lib/analytics";
import { onDeparture } from "@/lib/analytics/departure";
import type {
  CatalogPiece,
  CatalogShelf,
  ListPiecesParams,
  ProgramListItem,
} from "@/lib/api/api-client";
import { browseListParams, isBrowsing, libraryBrowsePath, parseBrowseState } from "@/lib/library/library-routes";
import { LibraryBrowse } from "./library-browse";
import { LibraryEmpty } from "./library-empty";
import { LibraryHome } from "./library-home";
import { LibrarySearchField, useSearchDraft } from "./library-search-field";
import { MineMatches } from "./mine-matches";

/**
 * L'orchestration de `/library` : lire l'URL, décider quoi montrer.
 *
 * # Une route, deux vues
 *
 * `/library` rend l'accueil en sélections ; `/library?q=…`, `?genre=…` ou
 * `?sort=…` rendent une liste paginée. Deux routes distinctes auraient démonté
 * le champ de recherche au moment précis où l'on tape dedans — la frappe
 * déclenche la bascule.
 *
 * # La recherche passe aussi par « Mes morceaux »
 *
 * Qui tape « Havana » cherche d'abord *son* Havana, celui qu'il a importé ou
 * qu'on lui a partagé, et qui n'est pas au catalogue. Ses morceaux passent
 * donc devant, en un bloc court, et le catalogue suit sous son propre titre.
 *
 * # Le champ de recherche est monté une seule fois
 *
 * Il vit ici, au-dessus des deux vues, pour la même raison : c'est ce qui
 * garantit que le focus et le curseur survivent au passage accueil → liste. Sa
 * valeur est locale et pilote l'URL après un temps mort, l'URL restant la
 * source de vérité de ce qui est *interrogé*.
 *
 * # Les sélections sont chargées ici
 *
 * L'accueil et la sidebar les lisent toutes deux, dans la même frame. Les
 * charger au niveau du parent leur évite de se les redemander l'une l'autre.
 * Ouvrir une sélection, en revanche, est devenu une navigation : elle a sa
 * propre page, rendue par le serveur (`/library/selection/…`).
 */

/**
 * Durée de présence des résultats à l'écran en deçà de laquelle la recherche
 * n'a pas été lue.
 *
 * C'est ce seuil, et non le temps mort ci-dessus, qui sépare une recherche
 * d'une étape de frappe. Une hésitation de 300 ms au milieu d'un mot pousse
 * « noc » dans l'URL, en ramène les résultats, puis les remplace par ceux de
 * « nocturne » avant que quiconque ait pu les parcourir : compter les deux
 * ferait de `catalog_searched` un compteur de frappes — trois événements
 * facturés pour une seule recherche —, et sa longueur moyenne mesurerait la
 * vitesse de frappe plutôt que la demande. Une seconde de résultats immobiles,
 * en revanche, a été regardée.
 */
const SEARCH_READ_MS = 1000;

/**
 * Ce qu'on retient d'une recherche en attendant de savoir si elle a servi.
 *
 * Tiré de la taxonomie plutôt que recopié : le jour où l'événement gagne une
 * propriété, c'est ici que le compilateur le dira. `opened` en est retiré,
 * parce qu'il ne se connaît qu'à la clôture : le ranger ici laisserait croire
 * qu'il se décide à l'arrivée des résultats.
 */
type CatalogSearch = Omit<
  Extract<VirtuosaEvent, { name: "catalog_searched" }>["properties"],
  "opened"
>;

/** Une recherche mesurée, et l'instant où ses résultats ont paru. */
type PendingSearch = {
  readonly measured: CatalogSearch;
  /** Horodatage de l'arrivée des résultats à l'écran. */
  readonly shownAt: number;
};

/** Ses résultats sont restés assez longtemps à l'écran pour avoir été lus. */
function wasRead(pending: PendingSearch): boolean {
  return Date.now() - pending.shownAt >= SEARCH_READ_MS;
}

export function LibraryCatalog({
  shelves,
  introProgram,
}: {
  /**
   * Les sélections, décidées et lues par le serveur.
   *
   * Il n'y a donc plus d'état de chargement à afficher : la liste est là au
   * premier rendu, ou elle est vide parce que `/shelves` n'a pas répondu — et
   * ce cas-là a déjà son écran de repli.
   */
  shelves: readonly CatalogShelf[];
  /** Le parcours proposé à qui ne sait pas encore jouer. `null` si l'API n'a
   *  pas répondu : l'accueil se passe alors de l'interstitiel. */
  introProgram: ProgramListItem | null;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const state = useMemo(() => parseBrowseState(searchParams), [searchParams]);

  const museScoreEnabled = useFeatureFlag("musescore_integration");
  const browsing = isBrowsing(state);

  const [draft, setDraft] = useSearchDraft(state.query, (next) => {
    // `replace` et non `push` : une recherche se raffine lettre par lettre, et
    // empiler une entrée d'historique par frappe rendrait le bouton retour
    // inutilisable — il faudrait le presser huit fois pour sortir d'un mot.
    router.replace(libraryBrowsePath({ ...state, query: next }), { scroll: false });
  });

  /** Combien de ses propres morceaux répondent à la recherche affichée. */
  const [mineFound, setMineFound] = useState(0);

  /**
   * « Le catalogue a été ouvert ». `library_opened` existait déjà mais n'a
   * **jamais** été émis avec `tab: "catalog"` — seul l'onglet « Mes morceaux »
   * l'émettait. Personne ne sait donc rien de l'usage réel du catalogue, ce
   * qui rend impossible de juger si cette refonte a servi à quelque chose.
   *
   * Une fois par montage, dès que le premier contenu est connu : `itemCount`
   * dit combien de morceaux le premier écran avait à offrir, ce qui distingue
   * « ouvert et vide » d'« ouvert et fourni ».
   */
  const opened = useRef(false);

  useEffect(() => {
    if (opened.current) {
      return;
    }

    opened.current = true;
    track({
      name: "library_opened",
      properties: {
        tab: "catalog",
        itemCount: shelves.reduce((sum, shelf) => sum + shelf.items.length, 0),
      },
    });
  }, [shelves]);

  /**
   * La recherche affichée, retenue le temps de savoir si elle a été lue, et si
   * elle a servi.
   *
   * Trois précautions tiennent dans cette `ref`. La mesure n'est pas composée à
   * la frappe — le champ est débouncé, et un événement par caractère serait un
   * compteur de frappes facturé. Elle est composée à l'**arrivée des
   * résultats**, à partir des critères que le serveur a honorés pour *cette*
   * réponse : sans quoi `queryLength` se lirait dans l'état courant, qui a pu
   * avancer d'une frappe pendant l'aller-retour, et l'événement décrirait une
   * requête avec le compte d'une autre. Elle n'est enfin émise qu'à la clôture,
   * parce qu'`opened` ne se sait qu'après.
   *
   * ⚠️ Le texte tapé n'entre jamais ici, seulement sa longueur. C'est la même
   * promesse que pour le formulaire de retour : on mesure la forme de la
   * demande, pas son contenu.
   */
  const pendingSearch = useRef<PendingSearch | null>(null);

  /**
   * Clôt la recherche en attente : elle part si elle a été lue, elle est
   * oubliée sinon.
   *
   * Ouvrir un résultat vaut lecture, si prompt que le clic ait été : on ne
   * clique pas dans une liste qu'on n'a pas regardée.
   */
  const closeSearch = useCallback((resultOpened: boolean) => {
    const pending = pendingSearch.current;

    if (pending === null) {
      return;
    }

    pendingSearch.current = null;

    if (!resultOpened && !wasRead(pending)) {
      return;
    }

    track({
      name: "catalog_searched",
      properties: { ...pending.measured, opened: resultOpened },
    });
  }, []);

  const searchResults = useCallback(
    (resultCount: number, params: ListPiecesParams) => {
      /*
        La précédente quitte l'écran à l'instant où celle-ci s'affiche : si elle
        a été lue, elle part sans avoir rien fait ouvrir ; si elle n'a été
        qu'une étape de frappe, elle est oubliée. C'est ce qui laisse une seule
        mesure quand on affine « noc » → « nocturne » d'un trait, celle qui a
        été lue.
      */
      closeSearch(false);

      const query = params.search ?? "";

      if (query === "") {
        /*
          Un genre ou un tri seuls ne sont pas une demande formulée. Les
          compter comme des recherches ferait tomber la longueur moyenne à
          zéro et noierait le seul chiffre qui compte ici : le catalogue
          contient-il ce qu'on lui demande.
        */
        return;
      }

      pendingSearch.current = {
        measured: {
          queryLength: query.length,
          resultCount,
          // Le genre est le seul critère qui restreigne : un tri ne retire
          // aucun morceau de la réponse.
          hasFilters: params.genre !== undefined,
        },
        shownAt: Date.now(),
      };
    },
    [closeSearch],
  );

  const searchResultOpened = useCallback(() => {
    closeSearch(true);
  }, [closeSearch]);

  useEffect(() => {
    /*
      Le départ de l'onglet clôt la recherche restée à l'écran.

      `onDeparture` plutôt qu'un `pagehide` posé à la main : sur la tablette du
      pupitre, `pagehide` n'arrive pas quand l'application passe en
      arrière-plan puis est tuée par l'OS, et la recherche qu'on venait de lire
      disparaîtrait sans laisser de trace — un biais qui frappe précisément
      l'appareil que le produit vise. Le vidage de la file est fait là-bas, et
      il n'est pas facultatif : l'écouteur du client d'analytique a été posé au
      démarrage, donc avant celui-ci, et l'événement atterrirait dans une file
      que plus personne ne viendrait chercher.
    */
    const disarm = onDeparture({
      close: () => {
        const pending = pendingSearch.current;

        /*
          Pas encore lue : on ne la clôt pas. Contrairement à une recherche
          chassée par la suivante, ses résultats n'ont pas disparu — ils seront
          encore là au retour au premier plan, et c'est alors qu'ils seront lus.
          La jeter ici perdrait la recherche de quiconque bascule une seconde
          sur une autre application avant de regarder.
        */
        if (pending === null || !wasRead(pending)) {
          return;
        }

        closeSearch(false);
      },
      reopen: () => {
        const pending = pendingSearch.current;

        if (pending === null) {
          return;
        }

        // La lecture recommence maintenant : les minutes passées en
        // arrière-plan ne sont du temps de lecture pour personne, et les
        // compter ferait passer n'importe quelle étape de frappe pour une
        // recherche lue.
        pendingSearch.current = { ...pending, shownAt: Date.now() };
      },
    });

    return () => {
      disarm();
      // On quitte le catalogue : ce qui restait en attente n'a rien ouvert.
      closeSearch(false);
    };
  }, [closeSearch]);

  /**
   * Le rebond de l'écran vide : les premiers morceaux de la première sélection.
   *
   * Pris dans ce qui est déjà chargé, jamais demandé pour l'occasion — cet
   * écran s'affiche après un échec, il ne doit rien faire attendre de plus.
   */
  const suggestions: readonly CatalogPiece[] = shelves[0]?.items.slice(0, 3) ?? [];

  /*
    Ses propres morceaux ont répondu : la recherche a abouti. L'en-tête
    « Dans le catalogue · Aucun morceau » suffit alors, et proposer d'aller
    chercher ailleurs — MuseScore, les suggestions — reviendrait à dire qu'on
    n'a rien trouvé.
  */
  const emptyState = mineFound > 0 && state.query !== "" ? null : (
    <LibraryEmpty
      query={state.query}
      hasFilters={state.genre !== null}
      museScoreEnabled={museScoreEnabled}
      suggestions={suggestions}
    />
  );

  return (
    <div className="mx-auto w-full max-w-[1180px] px-5 md:px-8 py-8 flex flex-col gap-8">
      <header className="flex flex-col gap-4">
        {/*
          La question d'abord, la recherche ensuite. Un champ de recherche seul
          demande de savoir quoi taper ; la question dit qu'on a le droit de ne
          pas savoir, et les sélections juste dessous y répondent.

          Masquée dès qu'on parcourt : elle appartient à l'accueil, et
          au-dessus d'une liste de résultats elle ne serait plus qu'un bandeau.
        */}
        {!browsing && (
          <h1 className="text-[32px] font-light tracking-[-0.02em] text-text-heading leading-tight">
            Qu&apos;est-ce que tu as envie de jouer&nbsp;?
          </h1>
        )}

        <LibrarySearchField
          value={draft}
          onChange={setDraft}
          placeholder="Un titre, un compositeur…"
          label="Rechercher dans le catalogue et dans mes morceaux"
        />
      </header>

      {browsing && state.query !== "" && (
        <MineMatches query={state.query} onFound={setMineFound} />
      )}

      {browsing ? (
        <LibraryBrowse
          params={browseListParams(state)}
          title={mineFound > 0 && state.query !== "" ? "Dans le catalogue" : browseTitle(state.query)}
          emptyState={emptyState}
          onResults={searchResults}
          onResultOpened={searchResultOpened}
        />
      ) : shelves.length === 0 ? (
        <ShelvesFallback emptyState={emptyState} drafts={state.drafts} />
      ) : (
        <LibraryHome shelves={shelves} drafts={state.drafts} introProgram={introProgram} />
      )}
    </div>
  );
}

function browseTitle(query: string): string {
  if (query !== "") {
    return `Résultats pour « ${query} »`;
  }

  return "Tout le répertoire";
}

/**
 * `/shelves` est tombé.
 *
 * On ne montre pas un écran blanc ni un message d'erreur seul : on montre le
 * catalogue trié par jouabilité, avec une ligne discrète qui dit que la
 * sélection du jour manque à l'appel. Quelqu'un qui vient chercher un morceau
 * en trouve un ; c'est la curation qui est absente, pas le répertoire.
 */
function ShelvesFallback({ emptyState, drafts }: { emptyState: React.ReactNode; drafts: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12px] text-text-muted">
        La sélection du moment n&apos;a pas pu être chargée. Voici le répertoire, du plus abordable au plus exigeant.
      </p>
      <LibraryBrowse params={{ sort: "playability", drafts }} title="Tout le répertoire" emptyState={emptyState} />
    </div>
  );
}
